import type { ReactElement, ReactNode } from 'react';
import { formatMoney, type Money } from '@fleet/shared';
import { useAppConfig } from '@/app/appConfig';

/**
 * Data display primitives.
 *
 * Money and every numeric column use tabular numerals so digits stack down the
 * column. Identifiers — plates, booking references, invoice numbers, odometer
 * readings — are set in monospace, because they are things you read aloud over
 * the phone rather than words you read in a sentence.
 */

/** A monetary amount. Formatting happens here and nowhere else. */
export function MoneyText({ amount }: { amount: Money }): ReactElement {
  const { locale } = useAppConfig();
  return (
    <span className="tnum">
      {formatMoney(amount, { prefix: locale.currencyPrefix, grouping: locale.numberGrouping })}
    </span>
  );
}

/** Plate, booking reference, invoice number, odometer reading. */
export function Identifier({ children }: { children: ReactNode }): ReactElement {
  return <span className="identifier">{children}</span>;
}

/**
 * A counted figure with its label — the row across the top of the day board.
 * The number is large and tabular; the label is small and quiet.
 */
interface FigureProps {
  readonly label: string;
  /**
   * A count, or something already formatted.
   *
   * Counts arrive as numbers from the day board. The money screens pass a
   * `<MoneyText>` instead, so the grouping and the "Rs" come from the one
   * place that knows how to write them rather than being rebuilt here.
   */
  readonly value: number | ReactNode;
  readonly emphasis?: 'neutral' | 'attention' | 'alert';
}

export function Figure({ label, value, emphasis = 'neutral' }: FigureProps): ReactElement {
  const valueColour =
    emphasis === 'alert' ? 'text-alert' : emphasis === 'attention' ? 'text-attention' : 'text-ink';
  return (
    <div className="flex flex-col gap-0.5 px-4 py-3">
      <span className="text-micro font-medium uppercase text-ink-muted">{label}</span>
      <span className={`tnum text-figure font-semibold ${valueColour}`}>{value}</span>
    </div>
  );
}
